import { Check, MoreHorizontal, X } from "lucide-react";

import { SortableTableHead, type SortDir } from "@/components/sortable-table-head";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AdminUserSummary } from "@/lib/api";
import { formatRelativeTime } from "@/lib/format";

function getInitials(user: AdminUserSummary): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
  }
  if (user.full_name) {
    return user.full_name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return (user.email[0] ?? "?").toUpperCase();
}

interface UserTableProps {
  users: AdminUserSummary[];
  sortBy: string | null;
  sortDir: SortDir;
  onSort: (column: string) => void;
  onView: (user: AdminUserSummary) => void;
  onEdit: (user: AdminUserSummary) => void;
  onResetPassword: (user: AdminUserSummary) => void;
  onRevokeSessions: (user: AdminUserSummary) => void;
  onDelete: (user: AdminUserSummary) => void;
}

export function UserTable({
  users,
  sortBy,
  sortDir,
  onSort,
  onView,
  onEdit,
  onResetPassword,
  onRevokeSessions,
  onDelete,
}: UserTableProps) {
  if (users.length === 0) {
    return <div className="text-muted py-12 text-center text-sm">No users found.</div>;
  }

  const sortProps = { sortBy, sortDir, onSort };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTableHead column="full_name" label="User" {...sortProps} />
          <SortableTableHead column="role" label="Role" {...sortProps} />
          {/* Status is derived from invited_at/email_verified — not a sortable column. */}
          <TableHead>Status</TableHead>
          <SortableTableHead column="last_active_at" label="Last Active" {...sortProps} />
          <SortableTableHead
            column="email_verified"
            label="Verified"
            className="text-center"
            {...sortProps}
          />
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => (
          <TableRow key={user.id} className="cursor-pointer" onClick={() => onView(user)}>
            <TableCell>
              <div className="flex items-center gap-3">
                <Avatar size="sm">
                  <AvatarFallback className="bg-accent-light text-primary text-[10px] font-semibold">
                    {getInitials(user)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-foreground truncate text-sm font-medium">
                    {user.full_name || user.email.split("@")[0]}
                  </p>
                  <p className="text-muted truncate text-xs">{user.email}</p>
                </div>
              </div>
            </TableCell>
            <TableCell>
              <Badge variant={user.role === "admin" ? "default" : "secondary"}>{user.role}</Badge>
            </TableCell>
            <TableCell>
              {user.invited_at && !user.email_verified ? (
                <Badge
                  variant="secondary"
                  className="border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
                >
                  Invited
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300"
                >
                  Active
                </Badge>
              )}
            </TableCell>
            <TableCell className="text-muted text-sm">
              {formatRelativeTime(user.last_active_at)}
            </TableCell>
            <TableCell className="text-center">
              {user.email_verified ? (
                <span className="inline-flex size-5 items-center justify-center rounded-full bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400">
                  <Check className="size-3" />
                </span>
              ) : (
                <span className="inline-flex size-5 items-center justify-center rounded-full bg-red-50 text-red-500 dark:bg-red-950 dark:text-red-400">
                  <X className="size-3" />
                </span>
              )}
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="size-4" />
                    <span className="sr-only">Actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <DropdownMenuItem onClick={() => onView(user)}>View details</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onEdit(user)}>Edit user</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onResetPassword(user)}>
                    Send password reset
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onRevokeSessions(user)}>
                    Sign out everywhere
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={() => onDelete(user)}>
                    Delete user
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
